import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { UserContext } from "../services/UserContext";
import { API, authHeaders } from "../services/config";
import './PacientesPage.css';

function emptyProfesional() {
  return {
    id: null,
    apellido: "",
    nombre: "",
    dni: "",
    mail: "",
    celular: "",
    matricula: "",
    color: "#032548",
    activo: true,
  };
}

/** Normaliza fechas del API (YYYY-MM-DD o ISO) a YYYY-MM-DD */
function toDateOnly(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

/** Formatea una fecha para mostrar: DD/MM/YYYY */
function fmtFecha(value) {
  const iso = toDateOnly(value);
  if (!iso || iso.length < 10) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function ProfesionalesPage() {
  const { user } = useContext(UserContext);
  const isAdmin = user?.role === "admin";

  const [profesionales, setProfesionales] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyProfesional());
  const [busqueda, setBusqueda] = useState("");
  const [error, setError] = useState("");
  const [licenciasProf, setLicenciasProf] = useState(null);
  const navigate = useNavigate();

  const fetchProfesionales = useCallback(async () => {
    try {
      const res = await fetch(`${API}/profesionales`, { headers: authHeaders() });
      if (res.status === 401) { navigate("/login"); return; }
      const data = await res.json();
      if (data.success) setProfesionales(data.data);
    } catch (e) {
      console.error("Error al cargar profesionales:", e);
    }
  }, [navigate]);

  useEffect(() => { fetchProfesionales(); }, [fetchProfesionales]);

  const handleAdd = () => {
    setForm(emptyProfesional());
    setEditId(null);
    setError("");
    setShowForm(true);
  };

  const handleEdit = (prof) => {
    setForm({ ...emptyProfesional(), ...prof });
    setEditId(prof.id);
    setError("");
    setShowForm(true);
  };

  const handleLicencias = (prof) => {
    setLicenciasProf(prof);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.apellido.trim() || !form.nombre.trim()) {
      setError("Apellido y nombre son obligatorios.");
      return;
    }
    setError("");

    const url = editId ? `${API}/profesionales/${editId}` : `${API}/profesionales`;
    const method = editId ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const errData = await res.json();
        setError(errData.message || "Error al guardar");
        return;
      }

      await fetchProfesionales();
      if (isAdmin) {
        setShowForm(false);
      }
    } catch (e) {
      setError("Error de conexión");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este profesional?")) return;
    try {
      await fetch(`${API}/profesionales/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      await fetchProfesionales();
    } catch (e) {
      console.error("Error al eliminar:", e);
    }
  };

  const filtrados = profesionales.filter((p) => {
    const q = busqueda.toLowerCase();
    return (
      p.apellido?.toLowerCase().includes(q) ||
      p.nombre?.toLowerCase().includes(q) ||
      p.dni?.includes(q) ||
      p.matricula?.toLowerCase().includes(q)
    );
  });

  /* ──────────────────────────────────────────────────────── */
  /* Vista para profesional: solo su propia ficha            */
  /* ──────────────────────────────────────────────────────── */
  if (!isAdmin) {
    const miProf = profesionales.find((p) => p.id === user?.profesional?.id);

    return (
      <div className="pp-container">
        <Sidebar />
        <div className="pp-content">
          <h2 className="pp-title">Mi perfil profesional</h2>

          {!miProf ? (
            <p style={{ color: "#64748b", marginTop: 24 }}>Cargando datos…</p>
          ) : (
            <>
              {!showForm ? (
                <ul className="pp-lista">
                  <li className="pp-card">
                    <div className="pp-card-header">
                      <h3 className="pp-card-nombre" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            background: miProf.color || "#032548",
                            flexShrink: 0,
                          }}
                        />
                        {miProf.apellido}, {miProf.nombre}
                      </h3>
                      <span
                        style={{
                          fontSize: "0.8rem",
                          padding: "2px 10px",
                          borderRadius: 12,
                          background: miProf.activo ? "#dcfce7" : "#fee2e2",
                          color: miProf.activo ? "#166534" : "#991b1b",
                          fontWeight: 600,
                        }}
                      >
                        {miProf.activo ? "Activo" : "Inactivo"}
                      </span>
                    </div>

                    <div className="pp-info" style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "8px 24px" }}>
                      {miProf.matricula && <span><b>Mat.:</b> {miProf.matricula}</span>}
                      {miProf.dni && <span><b>DNI:</b> {miProf.dni}</span>}
                      {miProf.celular && <span><b>Cel:</b> {miProf.celular}</span>}
                      {miProf.mail && <span><b>Mail:</b> {miProf.mail}</span>}
                    </div>

                    <div className="pp-botones-inferiores" style={{ marginTop: 12 }}>
                      <button
                        onClick={() => handleEdit(miProf)}
                        className="boton-secundario boton-historia"
                      >
                        ✏️ Editar mis datos
                      </button>
                      <button
                        onClick={() => handleLicencias(miProf)}
                        className="boton-secundario boton-historia"
                      >
                        📅 Mis licencias
                      </button>
                      <button
                        onClick={() => navigate(`/profesionales/${miProf.id}/prestaciones`)}
                        className="boton-secundario boton-historia"
                      >
                        💊 Prestaciones
                      </button>
                    </div>
                  </li>
                </ul>
              ) : (
                <FormModal
                  editId={editId}
                  form={form}
                  error={error}
                  isAdmin={isAdmin}
                  handleChange={handleChange}
                  handleSave={handleSave}
                  onCancel={() => setShowForm(false)}
                />
              )}
            </>
          )}

          {licenciasProf && (
            <LicenciasModal
              profesional={licenciasProf}
              onClose={() => setLicenciasProf(null)}
            />
          )}
        </div>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────── */
  /* Vista para admin: lista completa                        */
  /* ──────────────────────────────────────────────────────── */
  return (
    <div className="pp-container">
      <Sidebar />
      <div className="pp-content">
        <h2 className="pp-title">Profesionales</h2>

        <div className="pp-busqueda-container">
          <div className="pp-busqueda-wrapper">
            <span className="pp-busqueda-icon">🔍</span>
            <input
              type="text"
              placeholder="Buscar por apellido, nombre, DNI o matrícula..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pp-busqueda-input"
            />
          </div>
          <button onClick={handleAdd} className="pp-btn-agregar">
            <span className="pp-btn-icon">+</span> Agregar Profesional
          </button>
        </div>

        <ul className="pp-lista">
          {filtrados.map((p) => (
            <li key={p.id} className="pp-card">
              <div className="pp-card-header">
                <h3
                  className="pp-card-nombre"
                  onClick={() => handleEdit(p)}
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                  title="Editar profesional"
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: p.color || "#032548",
                      flexShrink: 0,
                    }}
                  />
                  {p.apellido}, {p.nombre}
                  <span style={{ fontSize: "0.8em", opacity: 0.7 }}>✏️</span>
                </h3>
                <span
                  style={{
                    fontSize: "0.8rem",
                    padding: "2px 10px",
                    borderRadius: 12,
                    background: p.activo ? "#dcfce7" : "#fee2e2",
                    color: p.activo ? "#166534" : "#991b1b",
                    fontWeight: 600,
                  }}
                >
                  {p.activo ? "Activo" : "Inactivo"}
                </span>
              </div>

              <div className="pp-info" style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "8px 24px" }}>
                {p.matricula && <span><b>Mat.:</b> {p.matricula}</span>}
                {p.dni && <span><b>DNI:</b> {p.dni}</span>}
                {p.celular && <span><b>Cel:</b> {p.celular}</span>}
                {p.mail && <span><b>Mail:</b> {p.mail}</span>}
              </div>

              <div className="pp-botones-inferiores" style={{ marginTop: 12 }}>
                <button
                  onClick={() => handleEdit(p)}
                  className="boton-secundario boton-historia"
                >
                  ✏️ Editar
                </button>
                <button
                  onClick={() => handleLicencias(p)}
                  className="boton-secundario boton-historia"
                >
                  📅 Licencias
                </button>
                <button
                  onClick={() => navigate(`/profesionales/${p.id}/prestaciones`)}
                  className="boton-secundario boton-historia"
                >
                  💊 Prestaciones
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="boton-secundario"
                  style={{ background: "#fee2e2", color: "#991b1b" }}
                >
                  🗑️ Eliminar
                </button>
              </div>
            </li>
          ))}
          {filtrados.length === 0 && (
            <li style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>
              No hay profesionales registrados.
            </li>
          )}
        </ul>

        {showForm && (
          <FormModal
            editId={editId}
            form={form}
            error={error}
            isAdmin={isAdmin}
            handleChange={handleChange}
            handleSave={handleSave}
            onCancel={() => setShowForm(false)}
          />
        )}

        {licenciasProf && (
          <LicenciasModal
            profesional={licenciasProf}
            onClose={() => setLicenciasProf(null)}
          />
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Modal de edición de profesional                         */
/* ──────────────────────────────────────────────────────── */
function FormModal({ editId, form, error, isAdmin, handleChange, handleSave, onCancel }) {
  return (
    <div className="modal-historia">
      <div className="modal-historia-content">
        <h2>{editId ? "Editar Profesional" : "Nuevo Profesional"}</h2>

        <div className="modal-historia-grid">
          <input
            name="apellido"
            placeholder="Apellido (*)"
            value={form.apellido}
            onChange={handleChange}
          />
          <input
            name="nombre"
            placeholder="Nombre (*)"
            value={form.nombre}
            onChange={handleChange}
          />
          <input
            name="dni"
            placeholder="DNI"
            value={form.dni}
            onChange={handleChange}
          />
          <input
            name="matricula"
            placeholder="Matrícula profesional"
            value={form.matricula}
            onChange={handleChange}
          />
          <input
            name="mail"
            type="email"
            placeholder="Mail"
            value={form.mail}
            onChange={handleChange}
          />
          <input
            name="celular"
            placeholder="Celular"
            value={form.celular}
            onChange={handleChange}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "12px 0" }}>
          <label style={{ fontWeight: 600 }}>
            Color en calendario:&nbsp;
            <input
              type="color"
              name="color"
              value={form.color}
              onChange={handleChange}
              style={{ cursor: "pointer", width: 40, height: 32, border: "none", padding: 0 }}
            />
          </label>

          {isAdmin && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
              <input
                type="checkbox"
                name="activo"
                checked={form.activo}
                onChange={handleChange}
              />
              Activo
            </label>
          )}
        </div>

        {error && (
          <p style={{ color: "#dc2626", fontWeight: 600, margin: "8px 0" }}>{error}</p>
        )}

        <div className="modal-historia-buttons">
          <button onClick={onCancel} className="btn-cerrar">
            Cancelar
          </button>
          <button onClick={handleSave} className="btn-guardar">
            {editId ? "Guardar Cambios" : "Guardar Profesional"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Modal de licencias / días no disponibles                */
/* ──────────────────────────────────────────────────────── */
function LicenciasModal({ profesional, onClose }) {
  const [bloques, setBloques] = useState([]);
  const [bloquesLoading, setBloquesLoading] = useState(false);
  const [showNewBloque, setShowNewBloque] = useState(false);
  const [newBloque, setNewBloque] = useState({ fecha_inicio: "", fecha_fin: "", razon: "" });
  const [bloquesError, setBloquesError] = useState("");

  useEffect(() => {
    if (!profesional?.id) return;
    setBloquesLoading(true);
    fetch(`${API}/profesionales/${profesional.id}/bloques`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.success) setBloques(d.data); })
      .catch(() => {})
      .finally(() => setBloquesLoading(false));
  }, [profesional?.id]);

  const handleAddBloque = async () => {
    if (!newBloque.fecha_inicio || !newBloque.fecha_fin) {
      setBloquesError("Las fechas de inicio y fin son obligatorias.");
      return;
    }
    if (newBloque.fecha_fin < newBloque.fecha_inicio) {
      setBloquesError("La fecha de fin debe ser igual o posterior a la de inicio.");
      return;
    }
    setBloquesError("");
    try {
      const res = await fetch(`${API}/profesionales/${profesional.id}/bloques`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(newBloque),
      });
      const data = await res.json();
      if (data.success) {
        setBloques(prev => [...prev, data.data]);
        setNewBloque({ fecha_inicio: "", fecha_fin: "", razon: "" });
        setShowNewBloque(false);
      } else {
        setBloquesError(data.message || "Error al guardar el período.");
      }
    } catch {
      setBloquesError("Error de conexión.");
    }
  };

  const handleDeleteBloque = async (id) => {
    if (!window.confirm("¿Eliminar este período de licencia?")) return;
    try {
      const res = await fetch(`${API}/bloques/${id}`, { method: "DELETE", headers: authHeaders() });
      const data = await res.json();
      if (data.success) setBloques(prev => prev.filter(b => b.id !== id));
    } catch {}
  };

  const fmtRango = (inicio, fin) => {
    const a = fmtFecha(inicio);
    const b = fmtFecha(fin);
    if (a === b) return a;
    return `${a} – ${b}`;
  };

  return (
    <div className="modal-historia">
      <div className="modal-historia-content">
        <h2>Licencias y días no disponibles</h2>
        <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: "0.92rem" }}>
          {profesional.apellido}, {profesional.nombre}
        </p>

        {!showNewBloque && (
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={() => { setShowNewBloque(true); setBloquesError(""); }}
              className="pp-btn-agregar"
              style={{ fontSize: "0.88rem" }}
            >
              + Agregar período
            </button>
          </div>
        )}

        {showNewBloque && (
          <div className="licencias-form">
            <p className="licencias-form__title">Nuevo período</p>
            <div className="licencias-form__fechas">
              <label className="licencias-form__label">
                Desde
                <input
                  type="date"
                  className="pp-busqueda-input"
                  value={newBloque.fecha_inicio}
                  onChange={e => setNewBloque(p => ({ ...p, fecha_inicio: e.target.value }))}
                />
              </label>
              <label className="licencias-form__label">
                Hasta
                <input
                  type="date"
                  className="pp-busqueda-input"
                  value={newBloque.fecha_fin}
                  onChange={e => setNewBloque(p => ({ ...p, fecha_fin: e.target.value }))}
                />
              </label>
            </div>
            <input
              className="pp-busqueda-input"
              value={newBloque.razon}
              onChange={e => setNewBloque(p => ({ ...p, razon: e.target.value }))}
              placeholder="Motivo (ej: Vacaciones, Licencia médica…)"
              style={{ marginBottom: 10 }}
            />
            {bloquesError && (
              <p className="licencias-form__error">{bloquesError}</p>
            )}
            <div className="modal-historia-buttons">
              <button
                onClick={() => {
                  setShowNewBloque(false);
                  setNewBloque({ fecha_inicio: "", fecha_fin: "", razon: "" });
                  setBloquesError("");
                }}
                className="btn-cerrar"
              >
                Cancelar
              </button>
              <button onClick={handleAddBloque} className="btn-guardar">
                Guardar período
              </button>
            </div>
          </div>
        )}

        {bloquesLoading ? (
          <p style={{ color: "#64748b", fontSize: "0.9rem" }}>Cargando…</p>
        ) : bloques.length === 0 && !showNewBloque ? (
          <p className="licencias-empty">Sin períodos de licencia registrados.</p>
        ) : (
          <ul className="licencias-lista">
            {bloques.map(b => (
              <li key={b.id} className="licencias-item">
                <div className="licencias-item__info">
                  <span className="licencias-item__fechas">
                    {fmtRango(b.fecha_inicio, b.fecha_fin)}
                  </span>
                  {b.razon && (
                    <span className="licencias-item__motivo">{b.razon}</span>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteBloque(b.id)}
                  className="licencias-item__eliminar"
                >
                  Eliminar
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="modal-historia-buttons" style={{ marginTop: 20 }}>
          <button onClick={onClose} className="btn-cerrar">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
